import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import BackgroundWord from "./BackgroundWords";
import { useContent } from "../context/ContentContext";
import { FOUNDER_PORTRAIT } from "../lib/data";

export const About = () => {
  const { content } = useContent();
  const about = content?.about || {};
  const pillars = about.pillars || [];

  return (
    <section
      id="ueber-uns"
      data-testid="about-section"
      className="relative bg-[#050505] py-32 md:py-44 overflow-hidden grain"
    >
      <BackgroundWord word="Manifest" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-10 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
          <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
          {about.eyebrow || "01 — Über Uns"}
        </div>

        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <h2
              data-testid="about-title"
              className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl whitespace-pre-line text-glow-red"
            >
              {about.title}
            </h2>

            <p className="mt-10 text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
              {about.intro}
            </p>

            <Link
              to="/geschichte"
              data-testid="about-story-link"
              className="group relative mt-8 inline-flex items-center gap-3 bg-white text-black px-7 py-4 text-[11px] md:text-[12px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_28px_rgba(255,0,51,0.45)] transition-all"
            >
              <span className="relative z-10 group-hover:text-white transition-colors">
                Die Geschichte hinter Straßenwert
              </span>
              <ArrowUpRight
                size={14}
                className="relative z-10 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
              />
              <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            </Link>

            <div className="mt-14 grid grid-cols-2 gap-x-10 gap-y-8 max-w-2xl">
              {pillars.map((p, i) => (
                <motion.div
                  key={p.num + i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  data-testid={`pillar-${p.num}`}
                  className="border-t border-white/10 pt-5"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="text-[#ff0033] font-display font-black text-sm">{p.num}</span>
                    <h3 className="font-display font-bold uppercase tracking-tight text-lg">{p.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-zinc-500 leading-relaxed">{p.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[3/4] overflow-hidden border border-white/[0.08] group">
              <Link to="/geschichte" data-testid="about-portrait-link" className="block w-full h-full">
                <img
                  src={about.image_url || FOUNDER_PORTRAIT}
                  alt="Maurizio Rogait — Gründer Straßenwert"
                  className="w-full h-full object-cover grayscale opacity-90 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-[1200ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 z-10">
                  <div className="flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-white/70 font-semibold">
                    <span className="red-led" />
                    {about.caption_eyebrow || "Gründer · Maurizio Rogait"}
                  </div>
                  <div className="font-display font-black uppercase text-2xl mt-2">
                    {about.caption_text || "Die Geschichte →"}
                  </div>
                </div>
                <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#ff0033]/40 blur-3xl group-hover:bg-[#ff0033]/60 transition-colors duration-700" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
